export interface LocalLessonNoteDraft {
  summaryItems: string[];
  practiceSuggestions: string[];
}

const PRACTICE_INSTRUCTION = /^(?:please\s+)?(?:practise|practice|work on|work through|review|repeat|try|focus on|aim to|continue practising|continue practicing|keep practising|keep practicing)\b/i;
const SUMMARY_HEADING = /^(?:lesson\s+)?summary\s*:?\s*$/i;
const PRACTICE_HEADING = /^(?:suggested\s+)?(?:practice|practise|homework)\s*:?\s*$/i;
const INLINE_HEADING = /^(?:(?:lesson\s+)?summary|(?:suggested\s+)?(?:practice|practise|homework))\s*:\s*(.*)$/i;

function stripBullet(value: string): string {
  return value.replace(/^\s*[-*•](?:\s+|$)/, "").trim();
}

/** Split only on sentence punctuation. Text is never rewritten or supplemented. */
function splitStatements(value: string): string[] {
  const result: string[] = [];
  let start = 0;
  for (let index = 0; index < value.length - 1; index += 1) {
    const character = value[index] ?? "";
    if (character !== "." && character !== "!" && character !== "?") continue;
    if (!/\s/.test(value[index + 1] ?? "")) continue;
    let next = index + 1;
    while (next < value.length && /\s/.test(value[next] ?? "")) next += 1;
    if (next >= value.length || !/[A-Z0-9]/.test(value[next] ?? "")) continue;
    result.push(value.slice(start, index + 1).trim());
    start = next;
    index = next - 1;
  }
  const tail = value.slice(start).trim();
  if (tail) result.push(tail);
  return result;
}

/**
 * Local-only organization of text already entered by the tutor.
 * It makes no network request and never creates or publishes a lesson or task.
 */
export function formatLessonNoteLocally(content: string): LocalLessonNoteDraft {
  const draft: LocalLessonNoteDraft = { summaryItems: [], practiceSuggestions: [] };
  let explicitSection: "auto" | "summary" | "practice" = "auto";

  for (const rawLine of content.split(/\r?\n/)) {
    let line = stripBullet(rawLine);
    if (!line) continue;

    if (SUMMARY_HEADING.test(line)) {
      explicitSection = "summary";
      continue;
    }
    if (PRACTICE_HEADING.test(line)) {
      explicitSection = "practice";
      continue;
    }

    const inline = line.match(INLINE_HEADING);
    if (inline) {
      explicitSection = /practice|practise|homework/i.test(line.slice(0, line.indexOf(":"))) ? "practice" : "summary";
      line = inline[1]?.trim() ?? "";
      if (!line) continue;
    }

    const statements = splitStatements(line);
    for (const statement of statements) {
      const item = stripBullet(statement);
      if (!item) continue;
      if (explicitSection === "practice" || (explicitSection === "auto" && PRACTICE_INSTRUCTION.test(item))) {
        draft.practiceSuggestions.push(item);
      } else {
        draft.summaryItems.push(item);
      }
    }
  }

  return draft;
}

function bulletSection(heading: string, items: string[]): string {
  const cleanItems = items.map(stripBullet).filter(Boolean);
  if (cleanItems.length === 0) return "";
  return [heading, ...cleanItems.map((item) => `- ${item}`)].join("\n");
}

/** Serialize tutor-edited draft sections without adding lesson facts. */
export function buildFormattedLessonNote(draft: LocalLessonNoteDraft): string {
  return [
    bulletSection("Lesson summary", draft.summaryItems),
    bulletSection("Suggested practice", draft.practiceSuggestions),
  ].filter(Boolean).join("\n\n");
}
